"use client";
import { useDetectIOS } from "@/hooks/useDetectIOS";
import { useEffect, useRef, useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import type { OnProgressProps } from "react-player/base";
import ReactPlayer, { type ReactPlayerProps } from "react-player/lazy";
import styles from "./Player.module.css";

interface IPlayer extends ReactPlayerProps {
  setBorderRadius?: boolean;
}

export function Player({
  playing,
  url,
  controls,
  setBorderRadius = true,
  loop = false,
  muted = false,
  light = true,
}: IPlayer) {
  const [domLoaded, setDomLoaded] = useState(false);
  const [opacity, setOpacity] = useState(false);
  const [volume, setVolume] = useState(0.1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const playerRef = useRef<ReactPlayer | null>(null);
  const { isIOS } = useDetectIOS();
  const handle = useFullScreenHandle();

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number.parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const handleProgress = (state: OnProgressProps) => {
    setCurrentTime(state.playedSeconds);
  };

  const handleDuration = (duration: number) => {
    setDuration(duration);
  };

  const handleSeekStart = () => {
    setSeeking(true);
  };

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seekTo = Number.parseFloat(e.target.value);
    setCurrentTime(seekTo);

    if (playerRef.current) {
      playerRef.current.seekTo(seekTo);
    }
  };

  const handleSeekEnd = () => {
    setSeeking(false);
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!(playerRef.current || duration)) {
      return;
    }

    const progressBar = e.currentTarget;
    const clickPosition = e.nativeEvent.offsetX;
    const progressBarWidth = progressBar.offsetWidth;
    const seekTo = (clickPosition / progressBarWidth) * duration;

    playerRef.current?.seekTo(seekTo, "seconds");
  };

  function handleFullscreenButton() {
    if (handle.active) {
      return handle.exit();
    }
    return handle.enter();
  }

  function setControls() {
    if (isIOS && controls === false) {
      return false;
    }
    return isIOS;
  }

  useEffect(() => {
    setDomLoaded(true);
  }, []);

  useEffect(() => {
    if (isPlaying && opacity) {
      const timeoutId = setTimeout(() => {
        setOpacity(false);
      }, 1500);
      return () => clearTimeout(timeoutId);
    }
  }, [opacity, isPlaying]);

  return (
    <>
      <div className={styles.player}>
        <FullScreen handle={handle}>
          {domLoaded && (
            <ReactPlayer
              onClick={() => setOpacity(!opacity)}
              ref={playerRef}
              url={url}
              volume={volume}
              playing={muted ? playing : isPlaying}
              light={light}
              controls={setControls()}
              loop={loop}
              muted={muted}
              playIcon={PlayButton}
              onProgress={handleProgress}
              onDuration={handleDuration}
              onError={(error) => console.error("Video playback error:", error)}
              aria-label="player"
              title="video"
              playsinline={true}
              className={`${styles.video} ${
                setBorderRadius ? styles.border : ""
              }`}
              width="100%"
              height="100%"
              onPause={() => setOpacity(true)}
              onClickPreview={() => {
                setShowControls(true);
                setTimeout(() => setIsPlaying(true), 100);
              }}
            />
          )}
          {!isIOS && showControls && (
            <div
              onMouseMove={() => setOpacity(true)}
              onClick={() => setOpacity(!opacity)}
              onKeyDown={() => setOpacity(!opacity)}
              className={`${styles.controls} ${opacity ? styles.active : ""}`}
            >
              <div className={styles.controlPanel}>
                <button
                  className={styles.playButton}
                  type="button"
                  onClick={() => setIsPlaying(!isPlaying)}
                >
                  {isPlaying ? PauseButton : PlayButton}
                </button>
                <div className={styles.volume}>
                  <button
                    className={styles.soundButton}
                    onClick={() => setVolume(volume === 0.0 ? 0.1 : 0.0)}
                    type="button"
                  >
                    {volume === 0.0 ? MutedButton : MuteButton}
                  </button>
                  <input
                    className={styles.rangeVolume}
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={handleVolumeChange}
                  />
                </div>
                <div className={styles.progress}>
                  <input
                    type="range"
                    min={0}
                    max={duration}
                    step="any"
                    value={seeking ? currentTime : currentTime}
                    onChange={handleSeekChange}
                    onMouseDown={handleSeekStart}
                    onMouseUp={handleSeekEnd}
                    onClick={handleProgressBarClick}
                  />
                </div>
                <button
                  className={styles.fullscreenButton}
                  type="button"
                  onClick={handleFullscreenButton}
                >
                  {FullscreenButton}
                </button>
              </div>
            </div>
          )}
        </FullScreen>
      </div>
    </>
  );
}

const PlayButton = (
  <svg
    style={{ position: "absolute" }}
    role="img"
    aria-label="play-button"
    width="50"
    height="50"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.6432 12.8486C20.2699 12.4569 20.2699 11.5442 19.6432 11.1526L6.53 2.95682C5.86395 2.54054 5 3.01939 5 3.80482V20.1963C5 20.9818 5.86395 21.4606 6.53 21.0443L19.6432 12.8486Z"
      fill="white"
    />
  </svg>
);
const PauseButton = (
  <svg
    role="img"
    aria-label="play-button"
    width="50"
    height="50"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="4" y="3" width="6" height="18" rx="2" fill="white" />
    <rect x="14" y="3" width="6" height="18" rx="2" fill="white" />
  </svg>
);

const MuteButton = (
  <svg
    role="img"
    aria-label="mute"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.4188 3.12775C12.0806 2.65499 13 3.12811 13 3.94148V20.0551C13 20.8685 12.0806 21.3416 11.4188 20.8688L6 16.9983H3C1.89543 16.9983 1 16.1029 1 14.9983V8.99829C1 7.89373 1.89543 6.99829 3 6.99829H6L11.4188 3.12775Z"
      fill="white"
    />
    <path
      d="M15.9922 16.9047C17.2331 15.6416 17.9985 13.9098 17.9985 11.9993C17.9985 10.0808 17.2266 8.34256 15.9766 7.07812"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.7773 4.21875C20.768 6.20936 21.9992 8.95936 21.9992 11.9969C21.9992 15.0345 20.768 17.7845 18.7773 19.7751"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MutedButton = (
  <svg
    role="img"
    aria-label="muted"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17 3.94317C17 3.1298 16.0806 2.65667 15.4188 3.12944L10 6.99998H7C5.89543 6.99998 5 7.89541 5 8.99998V15C5 16.1045 5.89543 17 7 17H7.22832L17 9.39978V3.94317ZM17 11.9335L10.2533 17.1809L15.4188 20.8705C16.0806 21.3433 17 20.8702 17 20.0568V11.9335Z"
      fill="white"
    />
    <path
      d="M21 5L3 19"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FullscreenButton = (
  <svg
    role="img"
    aria-label="fullscreen-button"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_7259_4978)">
      <path
        d="M4 18C4 19.1 4.9 20 6 20H10V18H6V14H4V18ZM20 6C20 4.9 19.1 4 18 4H14V6H18V10H20V6ZM6 6H10V4H6C4.9 4 4 4.9 4 6V10H6V6ZM20 18V14H18V18H14V20H18C19.1 20 20 19.1 20 18Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_7259_4978">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
